import { default as React, useEffect, useState, useRef, memo } from 'react'
import { InputAdornment, IconButton, DialogTitle, ListItemIcon, DialogContent, CircularProgress, List, ListItem, ListItemText, Dialog, Box, TextField, Avatar, Typography } from '@mui/material'
import { Description, Newspaper, Close, Search } from '@mui/icons-material'
import { useNavigate } from 'react-router-dom'
import './CommunityNewsPage.css'
import { siteSearch } from './data/search.js'
import { formatBytes, truncateWithEllipsis } from './utils.js'
import { Tag } from './Tags.js'
import { getFileTypeIconAsUrl } from '@fluentui/react-file-type-icons'
import { Stack } from '@mui/system'
import InfoLinkPopper from './InfoLinkPopper.js'
import { decodeSharePointText } from './data/index.js'
import { getLibraryDriveFromConfig, getSiteConfigByItem } from '../community/siteProvider.js'

const SearchControl = (props = {searchScope: undefined, onClearSearchScope: undefined}) => {
  const navigate = useNavigate()
  const smartNavigate = (to, options) => {
    if (options?.external) {
      window.open(to, "_blank");
    } else {
      navigate(to, options);
    }
  };
  const anchorRef = useRef(null);
  const searchInputRef = useRef(null)
  const [search, setSearch] = useState('')
  const [results, setResults] = useState([])
  const [loading, setLoading] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [anchorPosition, setAnchorPosition] = useState({ top: 0, left: 0, width: 0 });
  const [additionalResults, setAdditionalResults] = useState();
  const [fullscreen, setFullscreen] = useState(false)

  const handleClearSearchScope = () => {
    if (props.onClearSearchScope !== undefined) {
      props.onClearSearchScope()
    }
  }

  const open = () => {
    setDialogOpen(true)
    setFullscreen(false)
  }

  const close = () => {
    setDialogOpen(false)
    setFullscreen(false)
    setSearch('')
    setResults([])
    setAdditionalResults([])
  }

  const handleSearch = async (query) => {
    if (!search) return;

    if (anchorRef.current) {
      const rect = anchorRef.current.getBoundingClientRect();
      setAnchorPosition({ top: rect.bottom, left: rect.left, width: rect.width });
    }

    setLoading(true);
    open()

    const _search = [search]
    if (props.searchScope) {
      _search.push(`AND "${props.searchScope}"`)
    }
     _search.push(`NOT DRAFT`)
    const res = await siteSearch(_search.join(' AND '))

    const shownResults = res.splice(0, 5)
    setResults(shownResults)
    setAdditionalResults(res)
    setLoading(false);
  };

  const getIconForResult = (resource) => {
    let documentIcon = <Description />
    if (resource.driveItem) {
      const fileName = resource.fields?.LinkFilename
      const size = resource.fields?.FileSizeDisplay
      const fileType = resource.fields?.DocIcon
      const createdByUser = resource.createdBy.user
      const createdDate = resource.createdDateTime
      documentIcon = (<Stack alignItems='center'>
        <Avatar sx={{ width: 28, height: 28 }} src={getFileTypeIconAsUrl({extension: `.${(fileType ?? '').toLowerCase()}`})} variant='square' />
        <InfoLinkPopper title='More Info' typography={{ fontSize: '0.6rem !important'}} content={
            <Stack rowGap={.5} >
              <Typography >File Name: {fileName} </Typography>
              <Typography >Size: {formatBytes(size)} </Typography>
              <Typography >Added by {createdByUser.displayName} on {new Date(createdDate).toLocaleDateString()}</Typography>
            </Stack>
            } sx={{ zIndex: '1301'}} />
        </Stack>)
    }

    return {
      article: <Newspaper />,
      document: documentIcon,
    }[resource.qsePortalHitType]
  }

  const getLinkForResult = (result) => {
    const { resource } = result
    if (!resource) return
    const siteConfig = getSiteConfigByItem(resource)
    if (!siteConfig) {
      console.error('Cannot find siteConfig for item ', resource)
      return
    }
    if (result?.fields?.ContentType === "Repost Page") {
        return [resource.webUrl, { external: true }]
    } else if (resource.webUrl?.indexOf('/SitePages/') > -1) {
      if (result?.fields?.QSEPortalNews) return [`/${siteConfig.sitePath}/news/${result.hitId}`]
      if (result?.fields?.QSEPortalOUCorner) return [`/${siteConfig.sitePath}/ou-corner/${result.hitId}`]
    }
    if (resource["@odata.type"] === "#microsoft.graph.driveItem") {
      const driveConfig = getLibraryDriveFromConfig(siteConfig, resource.parentReference.driveId)
      let route = `/${siteConfig.sitePath}/resource-library`
      if (driveConfig.route !== undefined) {
        route = `${route}/${driveConfig.route}`
      }
      return [`${route}?resourceId=${resource.listItem.id}`, {
        state: {
          [resource.listItem.id]: result
        }
      }]
    }
  }

  const resultListItem = (result, key) => {
    const title = result.fields?.Title ?? result.fields?.LinkFilename
    const description = result.driveItem ? decodeSharePointText(result.driveItem?.description) : result.fields?.Description
    return (<ListItem
    dense
    key={key}
    divider
    onClick={() => {
      close();
      smartNavigate(...getLinkForResult(result))
    }}
    className='search-result-item'
    sx={{ cursor: 'pointer' }}
  >
    <ListItemIcon>
      {getIconForResult(result)}
    </ListItemIcon>
    <ListItemText
      primary={title}
      primaryTypographyProps={{ variant: 'body1'}}      
      secondary={<Stack gap={.5}>
        { (description && description !== '') && 
          <>{truncateWithEllipsis(description, 50)}</>
        }
        { (!description || description === '') && 
        <>{truncateWithEllipsis(result.summary?.split('<ddd/>')?.[0], 50)}</>
        }
      </Stack>
      }
      secondaryTypographyProps={{ variant: 'body2'}}
    />
  </ListItem>)}

  useEffect(() => {
    if (props.searchScope) {
      searchInputRef.current.focus()
    }
  }, [props.searchScope])

  return (
    <Box >
      <Box ref={anchorRef} sx={{ display: 'flex', gap: 1 }}>
        <TextField
          sx={{
            width: '300px'
          }}
          variant="outlined"
          placeholder='Search'
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          inputRef={searchInputRef}
          InputProps={{
            startAdornment: props.searchScope ? (
              <InputAdornment sx={{ position: 'relative', margin: '0 2px'}} position='start'>
                <Tag tag={props.searchScope.replace(/category:/, '')} onDelete={() => handleClearSearchScope()} sx={{ '& .MuiChip-label': {fontSize: '.7rem !important'}}} />
              </InputAdornment>
            ) : undefined,
            endAdornment: search ? (
              <InputAdornment sx={{ position: 'absolute', right: '14px' }} position="end">
                <IconButton onClick={handleSearch} edge="end">
                  <Search />
                </IconButton>
              </InputAdornment>
            ) : undefined
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              handleSearch()
            }
          }}
        />
      </Box>

      <Dialog
        hideBackdrop={!fullscreen}
        fullScreen={fullscreen}
        open={dialogOpen}
        onClose={close}
        PaperProps={{
          sx: fullscreen ? {
            top: '0px',
            left: '0px',
            width: '90vw',
            height: '90vh',
            m: 0,
            p: 0
          } : {
            position: 'fixed',
            top: `${anchorPosition.top}px`,
            left: `calc(${anchorPosition.left}px - 200px)`,
            width: `calc(${anchorPosition.width}px + 200px)`,
            m: 0,
            p: 0,
          },
        }}
      >
        {fullscreen && <DialogTitle disableTypography display='flex' flexDirection='row-reverse'>
          <IconButton onClick={close}>
            <Close />
          </IconButton>
        </DialogTitle>}
        <DialogContent>
          {loading && 
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
              <CircularProgress />
            </Box>
          }
          { !loading && results.length > 0 &&
            <List>
              {results.map((result, index) => {
                if (result?.contentType?.name === 'Folder') return null
                return resultListItem(result, index)
              })}
              {fullscreen && additionalResults.map((result, index) => {
                if (result?.contentType?.name === 'Folder') return null
                return resultListItem(result, index)
              })}
              {!fullscreen && additionalResults?.length > 0 && <ListItem
                dense
                key={'addlresults-item'}
                divider
                onClick={() => setFullscreen(true)}
                className='search-result-item'
                sx={{ cursor: 'pointer' }}
              >
                <ListItemText
                  primary={`See all ${additionalResults.length + results.length} results...`}
                />
              </ListItem>}
            </List>
          }
          {!loading && results.length === 0 && 
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
              <Typography variant='body1'>No results found.</Typography>
            </Box>
          }
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default memo(SearchControl)